import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { getCurrentUser, logout as logoutApi, refreshSession } from "../api/authClient";

const AuthContext = createContext({
    user: null,
    loading: true,
    isAuthenticated: false,
    checkAuth: () => {},
    logout: () => {},
});

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const checkAuth = useCallback(async () => {
        try {
            const userData = await getCurrentUser();
            setUser(userData);
            return true;
        } catch (error) {
            // If unauthorized, try to refresh the token
            if (error.response?.status === 401) {
                try {
                    await refreshSession();
                    const userData = await getCurrentUser();
                    setUser(userData);
                    return true;
                } catch (refreshError) {
                    setUser(null);
                    return false;
                }
            }
            setUser(null);
            return false;
        } finally {
            setLoading(false);
        }
    }, []);

    const logout = useCallback(async () => {
        try {
            await logoutApi();
        } catch (error) {
            console.error("Logout error:", error);
        } finally {
            setUser(null);
        }
    }, []);

    useEffect(() => {
        checkAuth();
    }, [checkAuth]);

    const value = {
        user,
        loading,
        isAuthenticated: !!user,
        checkAuth,
        logout,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error("useAuth must be used within an AuthProvider");
    }
    return context;
}